import { useEffect, useRef, useState } from "react";
import type { QcChecklistCheck } from "@/lib/manufacturing/builderAssistantChecklistClient";
import { flushQcChecklistChecks, type PendingQcCheck } from "./flushQcChecklistChecks";

type SaveResult =
  | { ok: true; saved: { item_id: string; observation_id: string | null }[] }
  | { ok: false; error: string };

/** Saves marks in the background and leaves photos on their own queue. */
export function useQcChecklistAutosave({
  sessionId,
  checks,
  onSaved,
  onError,
}: {
  sessionId: string;
  checks: readonly QcChecklistCheck[];
  onSaved: (marks: Record<string, PendingQcCheck>, saved: { item_id: string; observation_id: string | null }[]) => void;
  onError: (message: string) => void;
}) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<Record<string, PendingQcCheck>>({});
  const pendingRef = useRef(pending);
  const notesRef = useRef(notes);
  const sessionRef = useRef(sessionId);
  const savedRef = useRef(onSaved);
  const errorRef = useRef(onError);
  const checksRef = useRef(checks);
  const tail = useRef(Promise.resolve());
  pendingRef.current = pending;
  notesRef.current = notes;
  sessionRef.current = sessionId;
  savedRef.current = onSaved;
  errorRef.current = onError;
  checksRef.current = checks;

  useEffect(() => {
    setNotes((current) => {
      let changed = false;
      const next = { ...current };
      for (const check of checks) {
        if (next[check.item_id] !== undefined) continue;
        next[check.item_id] = check.note;
        changed = true;
      }
      return changed ? next : current;
    });
  }, [checks]);

  function dropSaved(snapshot: Record<string, PendingQcCheck>, dropped: string[]) {
    setPending((current) => {
      let changed = false;
      const next = { ...current };
      for (const id of dropped) {
        if (current[id] !== snapshot[id]) continue;
        delete next[id];
        changed = true;
      }
      if (!changed) return current;
      pendingRef.current = next;
      return next;
    });
  }

  function enqueue(job: () => Promise<SaveResult>): Promise<SaveResult> {
    const run = tail.current.then(job, job);
    tail.current = run.then(() => undefined, () => undefined);
    return run;
  }

  function takePending() {
    const snapshot = pendingRef.current;
    pendingRef.current = {};
    setPending({});
    return snapshot;
  }

  function persistDraft(): Promise<SaveResult> {
    return enqueue(async () => {
      const snapshot = pendingRef.current;
      const session = sessionRef.current;
      if (Object.keys(snapshot).length === 0) return { ok: true, saved: [] };
      const saved = await flushQcChecklistChecks(session, snapshot);
      if (!saved.ok) {
        errorRef.current(saved.error);
        return saved;
      }
      savedRef.current(snapshot, saved.saved);
      const dropped = Object.entries(snapshot).flatMap(([id, mark]) => (pendingRef.current[id] === mark ? [id] : []));
      dropSaved(snapshot, dropped);
      return saved;
    });
  }

  useEffect(() => {
    if (Object.keys(pending).length === 0) return;
    const handle = window.setTimeout(() => {
      void persistDraft();
    }, 400);
    return () => window.clearTimeout(handle);
  }, [pending]);

  function remember(itemId: string, result: "pass" | "fail" | "na", patch: Partial<PendingQcCheck>) {
    const existing = pendingRef.current[itemId];
    const server = checksRef.current.find((row) => row.item_id === itemId);
    const base: PendingQcCheck = existing ?? {
      result: resultFrom(server?.result) ?? result,
      note: notesRef.current[itemId] ?? "",
    };
    const next = { ...pendingRef.current, [itemId]: { ...base, ...patch, result: patch.result ?? base.result } };
    pendingRef.current = next;
    setPending(next);
  }

  function mark(itemId: string, result: "pass" | "fail" | "na") {
    remember(itemId, result, { result, note: notesRef.current[itemId] ?? "" });
  }

  return {
    notes,
    pending,
    persistDraft,
    takePending,
    mark,
    onNote(itemId: string, value: string) {
      setNotes((current) => ({ ...current, [itemId]: value }));
      notesRef.current = { ...notesRef.current, [itemId]: value };
      const server = resultFrom(checksRef.current.find((row) => row.item_id === itemId)?.result);
      if (!pendingRef.current[itemId] && !server) return;
      remember(itemId, server ?? "pass", { note: value });
    },
  };
}

function resultFrom(value: string | undefined): "pass" | "fail" | "na" | null {
  if (value === "pass" || value === "fail" || value === "na") return value;
  return null;
}
